import React, { useEffect, useMemo, useState } from 'react';
import {
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiMapPin,
  FiMail,
  FiCheckCircle,
} from 'react-icons/fi';
import AdminHeader from '../components/AdminHeader';
import request from '../utils/request';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SCHEDULED_ADMIN_NAMES = ['zee', 'zoey'];

const scheduleArrayToMap = (schedule = []) => {
  if (!Array.isArray(schedule)) return schedule || {};
  return schedule.reduce((acc, slot) => {
    if (!slot?.day) return acc;
    acc[slot.day] = {
      start: slot.start || '',
      end: slot.end || '',
      off: !!slot.off,
    };
    return acc;
  }, {});
};

const serializeSchedule = (draft = {}) =>
  WEEKDAYS.map((day) => {
    const slot = draft[day] || {};
    return {
      day,
      start: slot.start || '',
      end: slot.end || '',
      off: !!slot.off,
    };
  });

const getEmployeeId = (employee) => employee?._id || employee?.id;

const getEmployeeName = (employee) => {
  const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();
  return fullName || employee?.fullName || employee?.displayName || employee?.name || '';
};

const getEmployeeEmail = (employee) =>
  employee?.workEmail || employee?.personalEmail || employee?.email || '';

const matchesTrackedAdmin = (employee) => {
  const haystack = [
    employee?.firstName,
    employee?.lastName,
    employee?.fullName,
    employee?.displayName,
    employee?.name,
    employee?.email,
    employee?.workEmail,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return SCHEDULED_ADMIN_NAMES.some((name) => haystack.includes(name));
};

const getTrackedAdminOrder = (employee) => {
  const haystack = [
    employee?.firstName,
    employee?.lastName,
    employee?.fullName,
    employee?.displayName,
    employee?.name,
    employee?.email,
    employee?.workEmail,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const index = SCHEDULED_ADMIN_NAMES.findIndex((name) => haystack.includes(name));
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export default function EmployeeScheduleAdmin() {
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [scheduleDraft, setScheduleDraft] = useState({});
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await request.get('/hosts');
      const list = data?.hosts || data?.items || data;
      const normalized = (Array.isArray(list) ? list : [])
        .filter((employee) => employee?.isAdmin)
        .filter(matchesTrackedAdmin)
        .sort((a, b) => getTrackedAdminOrder(a) - getTrackedAdminOrder(b));

      setEmployees(normalized);
      setSelectedId((prev) => {
        if (prev && normalized.some((employee) => getEmployeeId(employee) === prev)) {
          return prev;
        }
        return normalized[0] ? getEmployeeId(normalized[0]) : null;
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load admin schedules');
    } finally {
      setLoading(false);
    }
  };

  const selectedEmployee = useMemo(() => {
    return employees.find((emp) => getEmployeeId(emp) === selectedId);
  }, [employees, selectedId]);

  useEffect(() => {
    if (!selectedEmployee) return;
    setScheduleDraft(scheduleArrayToMap(selectedEmployee.schedule));
  }, [selectedEmployee]);

  const filteredEmployees = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) => {
      const name = getEmployeeName(emp).toLowerCase();
      const email = getEmployeeEmail(emp).toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [employees, filter]);

  const updateSchedule = (day, field, value) => {
    setScheduleDraft((prev) => ({
      ...prev,
      [day]: { ...(prev?.[day] || {}), [field]: value },
    }));
  };

  const clearDay = (day) => {
    setScheduleDraft((prev) => {
      const next = { ...prev };
      delete next[day];
      return next;
    });
  };

  const applyWeekdayTemplate = () => {
    const mon = scheduleDraft?.Mon;
    if (!mon?.start && !mon?.end) return;
    const template = { start: mon?.start || '', end: mon?.end || '', off: !!mon?.off };
    const next = { ...scheduleDraft };
    ['Tue', 'Wed', 'Thu', 'Fri'].forEach((day) => {
      next[day] = { ...template };
    });
    setScheduleDraft(next);
  };

  const handleSave = async () => {
    if (!selectedEmployee) return;
    setSaving(true);
    setError(null);
    setJustSaved(false);
    try {
      const { data } = await request.patch(
        `/hosts/${getEmployeeId(selectedEmployee)}`,
        { schedule: serializeSchedule(scheduleDraft) }
      );
      const updated = data?.host || data?.employee || data;
      setEmployees((prev) =>
        prev.map((emp) =>
          getEmployeeId(emp) === getEmployeeId(updated) ? { ...emp, ...updated } : emp
        )
      );
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex w-full h-screen overflow-auto">
      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader heading="Dispatcher Schedules" pages="Pages / Admin Users / Schedules" />
        </div>

        <div className="flex flex-1 gap-6 p-6">
          <aside className="w-80 bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                <FiSearch className="text-gray-400" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search Zee or Zoey"
                  className="flex-1 bg-transparent text-sm outline-none"
                />
                <button
                  onClick={fetchEmployees}
                  title="Refresh"
                  className="text-gray-400 hover:text-gray-700"
                >
                  <FiRefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-sm text-gray-500">Loading schedules...</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No matching admin users found.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredEmployees.map((emp) => {
                    const isActive = getEmployeeId(emp) === selectedId;
                    const name = getEmployeeName(emp);
                    return (
                      <button
                        key={getEmployeeId(emp)}
                        onClick={() => setSelectedId(getEmployeeId(emp))}
                        className={`w-full text-left px-4 py-3 flex flex-col gap-1 hover:bg-gray-50 ${
                          isActive ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold">
                              {(emp.firstName || name || '?')[0]}
                              {(emp.lastName || '')[0]}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {name}
                              </p>
                              <p className="text-xs text-gray-500 capitalize">{emp.role || 'Admin user'}</p>
                            </div>
                          </div>
                          <span
                            className={`text-[11px] px-2 py-1 rounded-full ${
                              emp.status === 'active'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {emp.status || 'active'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2">
                          {emp.location && (
                            <>
                              <FiMapPin className="w-3 h-3" />
                              <span>{emp.location}</span>
                            </>
                          )}
                          {emp.timezone && (
                            <>
                              <FiClock className="w-3 h-3" />
                              <span>{emp.timezone}</span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>

          <main className="flex-1 space-y-4">
            {error && (
              <div className="p-3 rounded-xl bg-red-50 text-red-700 border border-red-200">
                {error}
              </div>
            )}
            {justSaved && (
              <div className="p-3 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-2">
                <FiCheckCircle className="w-4 h-4" />
                Schedule saved
              </div>
            )}

            {!selectedEmployee && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center text-gray-500">
                Select Zee or Zoey to edit their schedule.
              </div>
            )}

            {selectedEmployee && (
              <>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-700 flex items-center justify-center text-lg font-semibold">
                      {(selectedEmployee.firstName || getEmployeeName(selectedEmployee) || '?')[0]}
                      {(selectedEmployee.lastName || '')[0]}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">
                        {getEmployeeName(selectedEmployee)}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="capitalize">{selectedEmployee.role || 'Admin user'}</span>
                        {getEmployeeEmail(selectedEmployee) && (
                          <span className="flex items-center gap-1">
                            <FiMail className="w-4 h-4" />
                            {getEmployeeEmail(selectedEmployee)}
                          </span>
                        )}
                        {selectedEmployee.timezone && (
                          <span className="flex items-center gap-1">
                            <FiClock className="w-4 h-4" />
                            {selectedEmployee.timezone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setScheduleDraft({})}
                      className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                      disabled={saving}
                    >
                      Clear week
                    </button>
                    <button
                      onClick={applyWeekdayTemplate}
                      className="px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
                      disabled={saving}
                    >
                      Copy Mon → Fri
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {saving ? 'Saving...' : 'Save schedule'}
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Weekly schedule</h2>
                      <p className="text-sm text-gray-500">
                        No conflict checks applied — times are saved as provided.
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      Use time inputs to set start/end for each day. Toggle Off for PTO/OOO.
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {WEEKDAYS.map((day) => {
                      const slot = scheduleDraft?.[day] || {};
                      return (
                        <div
                          key={day}
                          className="p-3 rounded-xl border border-gray-200 bg-gray-50 flex flex-col gap-2"
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold">{day}</p>
                            <button
                              onClick={() => clearDay(day)}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Clear
                            </button>
                          </div>
                          <div className="space-y-2">
                            <input
                              type="time"
                              value={slot.start || ''}
                              onChange={(e) => updateSchedule(day, 'start', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
                              placeholder="Start"
                            />
                            <input
                              type="time"
                              value={slot.end || ''}
                              onChange={(e) => updateSchedule(day, 'end', e.target.value)}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm"
                              placeholder="End"
                            />
                            <label className="flex items-center gap-2 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={!!slot.off}
                                onChange={(e) => updateSchedule(day, 'off', e.target.checked)}
                              />
                              Off / PTO
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
